import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

interface ExpandedRowContextValue {
    expandedRowKey: string | null;
    columnCount: number;
    toggleRow: (rowKey: string | number) => void;
    isExpanded: (rowKey: string | number) => boolean;
}

const ExpandedRowContext = createContext<ExpandedRowContextValue | null>(null);

export const ExpandedRowProvider: React.FC<{
    columnCount: number;
    children: React.ReactNode;
}> = ({ columnCount, children }) => {
    const [expandedRowKey, setExpandedRowKey] = useState<string | null>(null);

    const toggleRow = useCallback((rowKey: string | number) => {
        const key = String(rowKey);
        setExpandedRowKey((prev) => (prev === key ? null : key));
    }, []);

    const isExpanded = useCallback(
        (rowKey: string | number) => expandedRowKey === String(rowKey),
        [expandedRowKey]
    );

    const value = useMemo(
        () => ({
            expandedRowKey,
            columnCount,
            toggleRow,
            isExpanded,
        }),
        [expandedRowKey, columnCount, toggleRow, isExpanded]
    );

    return <ExpandedRowContext.Provider value={value}>{children}</ExpandedRowContext.Provider>;
};

export const useExpandedRow = (): ExpandedRowContextValue => {
    const ctx = useContext(ExpandedRowContext);
    if (!ctx) {
        throw new Error('useExpandedRow must be used within ExpandedRowProvider (AnimatedTable)');
    }
    return ctx;
};

export const useExpandedRowOptional = (): ExpandedRowContextValue | null => useContext(ExpandedRowContext);
